import React, {useState} from 'react';
import {
    BuildingIcon,
    CheckCircle2,
    Globe2,
    ArrowRight,
    Shield,
    Building2,
    Clock,
    Users,
    MapPin,
    Star
} from 'lucide-react';

import image from '../../Assets/Images/trial/15- (1).webp'
import {Helmet} from 'react-helmet-async';

const PricingCards = () => {
    const [selectedPlan, setSelectedPlan] = useState(1);
    const [isYearly, setIsYearly] = useState(false);

    const features = {
        support: "24/7 Support Available",
        setup: "Fast Setup Process",
        consultation: "Free Initial Consultation",
        assistance: "Visa Assistance"
    };

    const plans = [
        {
            id: 0,
            name: "Sharjah Free Zone",
            icon: Globe2,
            price: {
                monthly: 5900,
                yearly: 60500
            },
            description: "Perfect for startups and small businesses looking to establish in the UAE.",
            features: [
                "Inclusive of Office Lease Agreement for Banking purposes",
                "Permits 3 - 5 business activities",
                "Multiple shareholders allowed",
                "Affordable license renewal options",
                "Access to business support services",
                "Ideal for startups and SMEs"
            ],
            highlight: false
        },
        {
            id: 1,
            name: "Dubai Free Zone",
            icon: Building2,
            price: {
                monthly: 12500,
                yearly: 137500
            },
            description: "Ideal for businesses seeking premium benefits and maximum flexibility.",
            features: [
                "Option to upgrade visa package at a reduced rate (AED 1,850)",
                "Free desk space included in package",
                "Prestigious Dubai business address",
                "Extendable to unlimited visa options",
                "Single license for multiple business activities",
                "Combination of service and commercial licenses available",
                "Complete bank account setup assistance",
                "International recognition and credibility",
                "Access to global networking events"
            ],
            highlight: true
        },
        {
            id: 2,
            name: "Dubai Mainland",
            icon: BuildingIcon,
            price: {
                monthly: 13000,
                yearly: 143000
            },
            description: "Complete solution for businesses targeting the local UAE market.",
            features: [
                "LLC licenses with 100% foreign ownership",
                "No paid-up share capital required",
                "Business license issued within a day",
                "Up to 10 activities on a single license",
                "Flexible shareholding structures",
                "Quick and easy visa quota upgrade",
                "Complimentary company stamp",
                "Options for commercial, trading, or service activities",
                "0 balance corporate bank accounts",
                "Direct access to the UAE market"
            ],
            highlight: false
        }
    ];

    const stats = [
        {icon: Clock, value: "24/7", label: "Support"},
        {icon: Users, value: "1000+", label: "Clients"},
        {icon: MapPin, value: "100%", label: "Success Rate"},
        {icon: Star, value: "10+", label: "Years Experience"}
    ];

    return (
        <main className="bg-gradient-to-b from-white to-gray-50">
            <Helmet>
                <meta name="keywords" content="Setup Business in Dubai, Business Setup, Business Solutions in Dubai "/>
                <title>Business Solutions Dubai - Service Pricing</title>
                <meta name="description"
                      content="Business Solutions Dubai offers tailored business zones designed to accelerate your growth. From tech innovation to real estate, Dubai offers unparalleled opportunities for global entrepreneurs."/>
                <meta name="robots" content="index, follow"/>
                <meta name="lenguage" content="english"/>
                <meta name="publisher" content="Business Solutions Dubai"/>
                <link rel="canonical" href="https://businesssolutionsdubai.com/pricing"/>

            </Helmet>
            {/* Hero Section with Modern Background */}
            <div className="relative overflow-hidden pt-40">
                {/* Corporate Background SVG */}
                <div className="absolute inset-0 w-full h-full">
                    <svg
                        className="w-full h-full"
                        viewBox="0 0 1440 490"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="none"
                    >
                        {/* Background Gradient */}
                        <rect width="100%" height="100%" fill="url(#backgroundGradient)"/>

                        {/* Minimalistic Lines */}
                        <line x1="0" y1="150" x2="1440" y2="150" stroke="url(#lineGradient)" strokeWidth="3"
                              opacity="0.8"/>
                        <line x1="0" y1="450" x2="1440" y2="450" stroke="url(#lineGradient)" strokeWidth="3"
                              opacity="0.8"/>

                        {/* Subtle Overlapping Rectangles */}
                        <rect x="300" y="100" width="400" height="200" fill="url(#rectangleGradient1)" opacity="0.6"/>
                        <rect x="800" y="300" width="500" height="200" fill="url(#rectangleGradient2)" opacity="0.4"/>

                        <defs>
                            <linearGradient id="backgroundGradient" x1="0" y1="0" x2="0" y2="600"
                                            gradientUnits="userSpaceOnUse">
                                <stop stopColor="#85C1E9"/>
                                <stop offset="1" stopColor="#D6EAF8"/>
                            </linearGradient>
                            <linearGradient id="lineGradient" x1="0" y1="150" x2="1440" y2="150"
                                            gradientUnits="userSpaceOnUse">
                                <stop stopColor="#2874A6"/>
                                <stop offset="1" stopColor="#AED6F1"/>
                            </linearGradient>
                            <linearGradient id="rectangleGradient1" x1="300" y1="100" x2="700" y2="300"
                                            gradientUnits="userSpaceOnUse">
                                <stop stopColor="#5DADE2"/>
                                <stop offset="1" stopColor="#D6EAF8"/>
                            </linearGradient>
                            <linearGradient id="rectangleGradient2" x1="800" y1="300" x2="1300" y2="500"
                                            gradientUnits="userSpaceOnUse">
                                <stop stopColor="#2980B9"/>
                                <stop offset="1" stopColor="#A9CCE3"/>
                            </linearGradient>
                        </defs>
                    </svg>
                </div>


                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 relative">
                    <div className="text-center space-y-8">
                        <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-[#74ACDF]">
                            Business Setup in The United Arab Emirates
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-[#ffffff] to-gray-100 bg-clip-text text-transparent">
                            Price and Services for Business Setup in <br/> The United Arab Emirates
                        </h1>
                        <p className="max-w-2xl mx-auto text-gray-600">
                            We provide our best prices and services to suit your needs. Our prices are competitive
                            and transparent, and we offer a wide range of services to help you grow your business.
                        </p>

                        {/* Pricing Toggle */}
                        <div className="flex items-center justify-center space-x-4">
                            <span className={`text-sm ${!isYearly ? 'text-white font-medium' : 'text-gray-500'}`}>
                                Monthly Billing
                            </span>
                            <button
                                onClick={() => setIsYearly(!isYearly)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isYearly ? 'bg-[#74ACDF]' : 'bg-gray-200'
                                }`}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isYearly ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                            <span className={`text-sm ${isYearly ? 'text-white font-medium' : 'text-gray-500'}`}>
                                Annual Billing
                                <span
                                    className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Save 8%
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rest of the component remains the same */
            }
            {/* Pricing Cards */
            }
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                <div className="grid md:grid-cols-3 gap-8">
                    {plans.map((plan) => {
                        const Icon = plan.icon;
                        return (
                            <article
                                key={plan.id}
                                className={`relative rounded-2xl p-8 transition-all duration-300 ${selectedPlan === plan.id
                                    ? 'bg-white shadow-xl scale-105 border-2 border-[#74ACDF]'
                                    : 'bg-white/50 shadow-lg hover:shadow-xl border border-gray-100'
                                }`}
                                onMouseEnter={() => setSelectedPlan(plan.id)}
                            >
                                {plan.highlight && (
                                    <div className="absolute top-0 right-0 mt-4 mr-4">
                                        <span
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#74ACDF] text-white">
                                            Popular
                                        </span>
                                    </div>
                                )}

                                <div className="flex items-center space-x-4 mb-6">
                                    <div className="p-2 bg-[#74ACDF]/10 rounded-lg">
                                        <Icon className="h-6 w-6 text-[#74ACDF]"/>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                                </div>

                                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>

                                <div className="mb-6">
                                    <div className="flex items-baseline">
                                        <span className="text-gray-500 text-sm mr-2">AED</span>
                                        <span className="text-4xl font-bold text-gray-900">
                                            {isYearly ? plan.price.yearly : plan.price.monthly}
                                        </span>
                                        <span className="text-gray-500 text-sm ml-2">
                                            {isYearly ? '/year' : '/month'}
                                        </span>
                                    </div>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <CheckCircle2 className="h-5 w-5 text-[#74ACDF] mr-3 flex-shrink-0"/>
                                            <span className="text-gray-600 text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a href="https://wa.link/txunpz" target="_blank" rel="noopener noreferrer">
                                    <button
                                        className="w-full group relative px-6 py-3 rounded-lg text-white overflow-hidden">
                                        <div
                                            className="absolute inset-0 bg-gradient-to-r from-[#74ACDF] to-blue-600 transition-transform duration-300 group-hover:scale-105"/>
                                        <div className="relative flex items-center justify-center space-x-2">
                                            <span>Get Started</span>
                                            <ArrowRight
                                                className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"/>
                                        </div>
                                    </button>
                                </a>
                            </article>
                        );
                    })}
                </div>

                {/* Features Grid */}
                <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={index}
                                className="text-center p-6 rounded-lg bg-white shadow-lg border border-gray-100"
                            >
                                <div className="inline-flex p-3 rounded-full bg-[#74ACDF]/10 mb-4">
                                    <Icon className="h-6 w-6 text-[#74ACDF]"/>
                                </div>
                                <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                                <div className="text-sm text-gray-600">{stat.label}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Additional Features */}
                <div className="flex justify-around flex-col md:flex-row gap-16 md:gap-0 py-16">
                    <div className="h-full w-full md:w-[20vw] mt-20 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                        <h4 className="text-xl font-bold text-gray-900 mb-8 text-center">
                            All Plans Include
                        </h4>
                        <div className="flex justify-center align-center flex-col gap-16">
                            {Object.entries(features).map(([key, value]) => (
                                <div key={key} className="flex items-center space-x-3">
                                    <CheckCircle2 className="h-5 w-5 text-[#74ACDF]"/>
                                    <span className="text-gray-600">{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="w-full md:w-[40vh] flex justify-center align-middle">
                        <img src={image} alt="dubai-landscape" className="w-full rounded-2xl shadow-xl" loading="lazy"/>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default PricingCards;